package dairy.mynotes.navigation

import androidx.navigation.NavHostController

/**
 * Navigator - Single point of contact for all navigation in the app
 * This class handles all navigation logic and provides type-safe navigation methods
 *
 * Usage:
 * val navigator = Navigator(navController)
 * navigator.navigateToNotesList()
 * navigator.navigateToLogin(clearBackStack = true)
 */
class Navigator(private val navController: NavHostController) {

    // ==================== Authentication Navigation ====================

    /**
     * Navigate to Login screen
     * @param clearBackStack If true, clears all previous destinations
     */
    fun navigateToLogin(clearBackStack: Boolean = false) {
        navController.navigate(Screen.Login.route) {
            if (clearBackStack) {
                popUpTo(0) { inclusive = true }
            }
            launchSingleTop = true
        }
    }

    /**
     * Navigate to Registration screen
     */
    fun navigateToRegistration() {
        navController.navigate(Screen.Registration.route) {
            launchSingleTop = true
        }
    }

    // ==================== Main App Navigation ====================

    /**
     * Navigate to Notes List screen (main screen after login)
     * @param clearBackStack If true, clears all previous destinations (useful after login)
     */
    fun navigateToNotesList(clearBackStack: Boolean = false) {
        navController.navigate(Screen.NotesList.route) {
            if (clearBackStack) {
                popUpTo(0) { inclusive = true }
            }
            launchSingleTop = true
        }
    }

    /**
     * Navigate to Add Note screen
     */
    fun navigateToAddNote() {
        navController.navigate(Screen.AddNote.route) {
            launchSingleTop = true
        }
    }

    /**
     * Navigate to Update Note screen (edit mode)
     * @param noteId The ID of the note to edit
     */
    fun navigateToUpdateNote(noteId: String) {
        navController.navigate(Screen.UpdateNote.createRoute(noteId, isEditMode = true)) {
            launchSingleTop = true
        }
    }

    /**
     * Navigate to Update Note screen (view-only mode)
     * @param noteId The ID of the note to view
     */
    fun navigateToViewNote(noteId: String) {
        navController.navigate(Screen.UpdateNote.createRoute(noteId, isEditMode = false)) {
            launchSingleTop = true
        }
    }

    /**
     * Navigate to Update Category screen
     * @param categoryId The ID of the category to edit
     */
    fun navigateToUpdateCategory(categoryId: String) {
        navController.navigate(Screen.UpdateCategory.createRoute(categoryId)) {
            launchSingleTop = true
        }
    }



    // ==================== Generic Navigation ====================

    /**
     * Navigate to any screen by route
     * @param route The route string of the destination
     * @param clearBackStack If true, clears all previous destinations
     */
    fun navigateTo(route: String, clearBackStack: Boolean = false) {
        navController.navigate(route) {
            if (clearBackStack) {
                popUpTo(0) { inclusive = true }
            }
            launchSingleTop = true
        }
    }

    /**
     * Navigate back to previous screen
     * @return true if navigation was successful, false if back stack is empty
     */
    fun navigateBack(): Boolean {
        return navController.popBackStack()
    }

    /**
     * Navigate back to a specific screen
     * @param route The route to navigate back to
     * @param inclusive If true, also pops the destination route
     */
    fun navigateBackTo(route: String, inclusive: Boolean = false) {
        navController.popBackStack(route, inclusive)
    }

    /**
     * Clear entire back stack and navigate to a screen
     * Useful for logout or deep navigation changes
     */
    fun navigateAndClearBackStack(route: String) {
        navController.navigate(route) {
            popUpTo(0) { inclusive = true }
            launchSingleTop = true
        }
    }

    /**
     * Handle logout - clear back stack and go to login
     */
    fun logout() {
        navigateAndClearBackStack(Screen.Login.route)
    }

    /**
     * Handle successful login - clear back stack and go to notes list
     */
    fun onLoginSuccess() {
        navigateAndClearBackStack(Screen.NotesList.route)
    }

    /**
     * Handle successful registration - clear back stack and go to notes list
     */
    fun onRegistrationSuccess() {
        navigateAndClearBackStack(Screen.NotesList.route)
    }

    // ==================== Navigation State ====================

    /**
     * Get current route
     */
    fun getCurrentRoute(): String? {
        return navController.currentBackStackEntry?.destination?.route
    }

    /**
     * Check if current screen is a specific route
     */
    fun isCurrentRoute(route: String): Boolean {
        return getCurrentRoute() == route
    }

    /**
     * Check if user is on an auth screen (login or registration)
     */
    fun isOnAuthScreen(): Boolean {
        val currentRoute = getCurrentRoute()
        return currentRoute == Screen.Login.route || currentRoute == Screen.Registration.route
    }

    /**
     * Check if back stack is empty (user is on root screen)
     */
    fun isAtRoot(): Boolean {
        return navController.previousBackStackEntry == null
    }
}

